// Central SEO Configuration System
// This file contains all SEO metadata for all pages in the application

interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: string;
  canonical?: string;
}

interface SEOConfigs {
  [key: string]: SEOConfig;
}

const origin = "https://praxis-versichert.de";

export const seoConfigs: SEOConfigs = {
  // ===== MAIN PAGES =====
  '/': {
    title: 'Cyberversicherung für Arztpraxen | Praxis-Versichert.de',
    description: 'Cyberversicherung für Arztpraxen ✓ Schutz vor Ransomware & Datenverlust ✓ DSGVO-konform ✓ 24/7 IT-Notfallhilfe ✓ Ab 29€/Monat ✓ Vergleich 2025 - Jetzt beraten lassen!',
    keywords: 'Cyberversicherung Arztpraxis, Cyberversicherung Ärzte, IT-Sicherheit Praxis',
    image: `${origin}/assets/hero-medical-cyber-main.webp`,
    type: 'website',
    canonical: origin
  },
  
  '/ueber-uns': {
    title: 'Über uns - praxis-versichert.de | Ihr Partner für Cyberversicherungen',
    description: 'Erfahren Sie mehr über praxis-versichert.de und unser erfahrenes Netzwerk. Spezialisiert auf maßgeschneiderte Cyberversicherungen für Arztpraxen, Kliniken und MVZ.',
    canonical: `${origin}/ueber-uns`
  },
  
  '/team': {
    title: 'Unser Team - Experten für Cyberversicherungen | Praxis-Versichert.de',
    description: 'Lernen Sie unser Expertenteam kennen. Spezialisiert auf Cyberversicherungen für Arztpraxen, Kliniken und medizinische Einrichtungen mit über 100 Jahren kombinierter Erfahrung.',
    keywords: 'Cyberversicherung Team, Versicherungsexperten, Arztpraxis Beratung, Cyber-Sicherheit Berater',
    canonical: `${origin}/team`
  },
  
  '/faq': {
    title: 'Häufig gestellte Fragen - Cyberversicherung für Arztpraxen | Praxis-Versichert.de',
    description: 'Alle Antworten zur Cyberversicherung für Arztpraxen: Kosten, Leistungen, Vertragsdetails, Schadensabwicklung und mehr. Finden Sie schnell die Information, die Sie suchen.',
    keywords: 'Cyberversicherung FAQ, Häufige Fragen Cyberversicherung, Kosten Cyberversicherung Arztpraxis',
    canonical: `${origin}/faq`
  },
  
  '/datenschutz': {
    title: 'Datenschutzerklärung | Praxis-Versichert.de',
    description: 'Datenschutzerklärung von praxis-versichert.de - Umfassende Informationen zur transparenten Verarbeitung personenbezogener Daten gemäß DSGVO-Richtlinien.',
    canonical: `${origin}/datenschutz`
  },
  
  '/impressum': {
    title: 'Impressum | Praxis-Versichert.de',
    description: 'Impressum und Kontaktdaten von praxis-versichert.de - Ihr zuverlässiger Spezialist für professionelle Cyberversicherungen im deutschen Gesundheitswesen.',
    canonical: `${origin}/impressum`
  },
  
  '/newsletter': {
    title: 'Newsletter - Cyber-Sicherheit für Arztpraxen | Praxis-Versichert.de',
    description: 'Bleiben Sie stets informiert über aktuelle Cyber-Bedrohungen, bewährte Best Practices und wertvolle Versicherungstipps für Arztpraxen. Newsletter-Anmeldung hier.',
    canonical: `${origin}/newsletter`
  },
  
  '/termin-buchen': {
    title: 'Termin buchen - Kostenlose Beratung | Praxis-Versichert.de',
    description: 'Buchen Sie jetzt Ihren kostenlosen Beratungstermin. Unsere Cyber-Experten analysieren Ihre individuellen Risiken und finden die optimale Versicherung für Ihre Praxis.',
    canonical: `${origin}/termin-buchen`
  },
  
  '/sitemap': {
    title: 'Sitemap | Praxis-Versichert.de',
    description: 'Vollständige Übersicht aller Seiten auf praxis-versichert.de - Finden Sie schnell und einfach die gewünschte Information zur Cyberversicherung für Arztpraxen.',
    canonical: `${origin}/sitemap`
  },
  
  '/blog': {
    title: 'Blog - Cyber-Sicherheit & Versicherungstipps | Praxis-Versichert.de',
    description: 'Aktuelle Fachartikel zu Cyber-Sicherheit, DSGVO-Compliance, effektivem Ransomware-Schutz und praxisnahe Versicherungstipps für Arztpraxen, Kliniken und MVZ.',
    canonical: `${origin}/blog`
  },

  // ===== PRAXISTYPEN =====
  '/cyberversicherung/hausarztpraxis': {
    title: 'Cyberversicherung für Hausarztpraxen | Spezieller Schutz',
    description: 'Maßgeschneiderte Cyberversicherung speziell für Hausarztpraxen. Umfassender Schutz vor Ransomware, Datenverlust und Betriebsunterbrechung bereits ab 500€/Jahr.',
    canonical: `${origin}/cyberversicherung/hausarztpraxis`
  },
  
  '/cyberversicherung/facharztpraxis': {
    title: 'Cyberversicherung für Facharztpraxen | Spezialisierter Schutz',
    description: 'Hochspezialisierte Cyberversicherung für Facharztpraxen. Umfassender professioneller Schutz gegen alle Cyber-Risiken, DSGVO-Bußgelder und kostspielige IT-Ausfälle.',
    canonical: `${origin}/cyberversicherung/facharztpraxis`
  },
  
  '/cyberversicherung/zahnarztpraxis': {
    title: 'Cyberversicherung für Zahnarztpraxen | Digitaler Schutz',
    description: 'Professionelle Cyberversicherung speziell für Zahnarztpraxen. Maximaler Schutz für sensible Patientendaten, digitale Röntgenbilder und Behandlungsunterlagen.',
    canonical: `${origin}/cyberversicherung/zahnarztpraxis`
  },
  
  '/cyberversicherung/mvz': {
    title: 'Cyberversicherung für MVZ | Medizinische Versorgungszentren',
    description: 'Hochleistungs-Cyberversicherung für MVZ und Medizinische Versorgungszentren. Professioneller Schutz für große Datenmengen, mehrere Standorte und komplexe IT-Systeme.',
    canonical: `${origin}/cyberversicherung/mvz`
  },
  
  '/cyberversicherung/klinik': {
    title: 'Cyberversicherung für Kliniken | Krankenhausschutz',
    description: 'Premium-Cyberversicherung für Kliniken und Krankenhäuser. Rund-um-die-Uhr 24/7 Notfallschutz für kritische IT-Infrastruktur und sensible Patientendaten.',
    canonical: `${origin}/cyberversicherung/klinik`
  },

  // ===== STÄDTE =====
  '/cyberversicherung/berlin': {
    title: 'Cyberversicherung Berlin - Arztpraxen & Kliniken absichern',
    description: 'Professionelle Cyberversicherung für Berliner Arztpraxen und Kliniken. Lokale Expertenpräsenz, maßgeschneiderte Tarife und zuverlässiger 24/7 Notfallschutz vor Ort.',
    canonical: `${origin}/cyberversicherung/berlin`
  },
  
  '/cyberversicherung/hamburg': {
    title: 'Cyberversicherung Hamburg - Praxisschutz in der Hansestadt',
    description: 'Maßgeschneiderte Cyberversicherung für Hamburger Arztpraxen und Kliniken. Hochspezialisierte Beratung und optimaler Rundumschutz vor digitalen Cyber-Risiken.',
    canonical: `${origin}/cyberversicherung/hamburg`
  },
  
  '/cyberversicherung/muenchen': {
    title: 'Cyberversicherung München - Bayerns Praxen schützen',
    description: 'Premium-Cyberversicherung für Münchner Arztpraxen, Kliniken und MVZ. Lokale Münchner Expertise und umfassender professioneller Cyber-Schutz für bayerische Mediziner.',
    canonical: `${origin}/cyberversicherung/muenchen`
  },
  
  '/cyberversicherung/koeln': {
    title: 'Cyberversicherung Köln - Rheinische Praxen absichern',
    description: 'Spezialisierte Cyberversicherung für Kölner Arztpraxen und medizinische Einrichtungen. Professionelle Expertenberatung und individuell maßgeschneiderter Schutz.',
    canonical: `${origin}/cyberversicherung/koeln`
  },
  
  '/cyberversicherung/frankfurt': {
    title: 'Cyberversicherung Frankfurt - Main-Metropole schützen',
    description: 'Hochwertige Cyberversicherung für Frankfurter Arztpraxen, Kliniken und MVZ. Spezialisierte Premium-Absicherung im deutschen Finanz- und Gesundheitszentrum.',
    canonical: `${origin}/cyberversicherung/frankfurt`
  },

  // ===== SCHADENSFÄLLE =====
  '/cyberversicherung/ransomware-schutz': {
    title: 'Ransomware-Schutz für Arztpraxen | Cyberversicherung',
    description: 'Hochprofessioneller Ransomware-Schutz für medizinische Einrichtungen. Umfassende Absicherung vor Datenverschlüsselung, Lösegeldforderungen und totalem Datenverlust.',
    canonical: `${origin}/cyberversicherung/ransomware-schutz`
  },
  
  '/cyberversicherung/phishing-schutz': {
    title: 'Phishing-Schutz für Arztpraxen | Cyberversicherung',
    description: 'Effektiver Schutz Ihrer Praxis vor gefährlichen Phishing-Angriffen. Professionelle Mitarbeiter-Schulungen, technische Prävention und umfassender Versicherungsschutz.',
    canonical: `${origin}/cyberversicherung/phishing-schutz`
  },
  
  '/cyberversicherung/datenverlust': {
    title: 'Datenverlust-Schutz für Arztpraxen | Cyberversicherung',
    description: 'Professionelle Absicherung gegen kritischen Datenverlust in Arztpraxen. Schnelle Datenwiederherstellung, IT-Forensik und vollständige finanzielle Kompensation.',
    canonical: `${origin}/cyberversicherung/datenverlust`
  },
  
  '/cyberversicherung/dsgvo-bussgelder': {
    title: 'DSGVO-Bußgelder absichern | Cyberversicherung Arztpraxis',
    description: 'Wirksamer Schutz vor hohen DSGVO-Bußgeldern für Arztpraxen. Professionelle Rechtsberatung, aktive Compliance-Unterstützung und vollständige finanzielle Absicherung.',
    canonical: `${origin}/cyberversicherung/dsgvo-bussgelder`
  },
  
  '/cyberversicherung/betriebsunterbrechung': {
    title: 'Betriebsunterbrechung absichern | Cyberversicherung',
    description: 'Umfassende Absicherung bei längerer Betriebsunterbrechung durch schwere Cyberangriffe. Voller Schutz: Umsatzausfall, alle laufenden Kosten und Wiederanlaufkosten.',
    canonical: `${origin}/cyberversicherung/betriebsunterbrechung`
  },
  
  '/cyberversicherung/patientendaten-diebstahl': {
    title: 'Patientendaten-Diebstahl absichern | Cyberversicherung',
    description: 'Maximaler Schutz bei schwerem Diebstahl sensibler Patientendaten. Volle DSGVO-Konformität, Deckung von Schadensersatzforderungen und professionelles Reputationsmanagement.',
    canonical: `${origin}/cyberversicherung/patientendaten-diebstahl`
  },

  // ===== TARIFE =====
  '/tarife/allianz': {
    title: 'Allianz Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'Allianz Cyberversicherung im Detail: Alle Deckungssummen, umfassende Leistungen, transparente Konditionen und faire Preise für Arztpraxen. Jetzt kostenlos vergleichen!',
    canonical: `${origin}/tarife/allianz`
  },
  
  '/tarife/hdi': {
    title: 'HDI Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'HDI Cyberversicherung im detaillierten Überblick: Umfassender Premium-Schutz, transparente faire Konditionen und professionell schnelle Schadensregulierung für Arztpraxen.',
    canonical: `${origin}/tarife/hdi`
  },
  
  '/tarife/axa': {
    title: 'AXA Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'AXA Cyberversicherung: Hochleistungsstarke Premium-Absicherung für Arztpraxen mit umfangreichen wertvollen Zusatzleistungen und aktiver professioneller Prävention.',
    canonical: `${origin}/tarife/axa`
  },
  
  '/tarife/ergo': {
    title: 'ERGO Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'ERGO Cyberversicherung: Hochflexibler maßgeschneiderter Schutz für kleine und große Arztpraxen mit individuellen modularen Bausteinen und zuverlässiger 24/7 Notfallhilfe.',
    canonical: `${origin}/tarife/ergo`
  },
  
  '/tarife/gothaer': {
    title: 'Gothaer Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'Gothaer Cyberversicherung: Hochzuverlässiger erfahrener Partner mit umfassenden Premium-Leistungen und langjähriger spezialisierter Expertise für deutsche Mediziner.',
    canonical: `${origin}/tarife/gothaer`
  },
  
  '/tarife/hiscox': {
    title: 'Hiscox Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'Hiscox Cyberversicherung: Exklusiver Premium-Schutz für anspruchsvolle Arztpraxen mit weltweiter internationaler Deckung und hochprofessionellem 24/7 Experten-Support.',
    canonical: `${origin}/tarife/hiscox`
  },
  
  '/tarife/markel': {
    title: 'Markel Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'Markel Cyberversicherung: Hochspezialisierte maßgeschneiderte Lösungen für Arztpraxen mit maximal flexiblen Deckungsoptionen und transparenten fairen Versicherungsprämien.',
    canonical: `${origin}/tarife/markel`
  },
  
  '/tarife/zurich': {
    title: 'Zurich Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'Zurich Cyberversicherung: Langjährige internationale Premium-Expertise und umfassender professioneller Rundumschutz für Arztpraxen jeder Größe und Fachrichtung.',
    canonical: `${origin}/tarife/zurich`
  },
  
  '/tarife/alte-leipziger': {
    title: 'Alte Leipziger Cyberversicherung | Tarif & Leistungen',
    description: 'Alte Leipziger Cyberversicherung: Renommierter deutscher Traditionsversicherer mit hochmodernem innovativem Cyber-Schutz und persönlicher Betreuung für Arztpraxen.',
    canonical: `${origin}/tarife/alte-leipziger`
  },
  
  '/tarife/cogitanda': {
    title: 'Cogitanda Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'Cogitanda Cyberversicherung: Hochinnovative zukunftsorientierte Versicherungslösungen und erstklassige digitale Services speziell für moderne digitalisierte Arztpraxen.',
    canonical: `${origin}/tarife/cogitanda`
  },
  
  '/tarife/nuernberger': {
    title: 'Nürnberger Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'Nürnberger Cyberversicherung: Hochsolider zuverlässiger Rundumschutz mit transparenten attraktiven Konditionen und fairem Preis-Leistungs-Verhältnis für Arztpraxen.',
    canonical: `${origin}/tarife/nuernberger`
  },
  
  '/tarife/rv': {
    title: 'R+V Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'R+V Cyberversicherung: Bewährte genossenschaftliche Sicherheit mit solider Tradition und umfassender professioneller Rundumschutz für deutsche Arztpraxen jeder Größe.',
    canonical: `${origin}/tarife/rv`
  },
  
  '/tarife/signal-iduna': {
    title: 'Signal Iduna Cyberversicherung | Tarif & Leistungen',
    description: 'Signal Iduna Cyberversicherung: Hochzuverlässiger umfassender Versicherungsschutz mit persönlicher individueller Beratung und maßgeschneiderten Lösungen für Arztpraxen.',
    canonical: `${origin}/tarife/signal-iduna`
  },
  
  '/tarife/stoik': {
    title: 'Stoik Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'Stoik Cyberversicherung: Hochspezialisierte innovative Cyber-Versicherungslösungen mit modernster KI-gestützter Technologie und fortschrittlichen Sicherheitstools für Arztpraxen.',
    canonical: `${origin}/tarife/stoik`
  },
  
  '/tarife/vhv': {
    title: 'VHV Cyberversicherung für Arztpraxen | Tarif & Leistungen',
    description: 'VHV Cyberversicherung: Hochumfassende Premium-Absicherung mit professionell schneller Schadensregulierung und persönlichem Service für Arztpraxen in ganz Deutschland.',
    canonical: `${origin}/tarife/vhv`
  },
  
  '/tarife/wuerttembergische': {
    title: 'Württembergische Cyberversicherung | Tarif & Leistungen',
    description: 'Württembergische Cyberversicherung: Erfahrener regionaler Versicherungspartner mit ausgezeichneten starken Leistungen und persönlicher Betreuung für süddeutsche Arztpraxen.',
    canonical: `${origin}/tarife/wuerttembergische`
  },

  // ===== WEITERE VERSICHERUNGEN =====
  '/versicherungen/berufshaftpflicht': {
    title: 'Berufshaftpflicht für Ärzte | Praxis-Versichert.de',
    description: 'Professionelle Berufshaftpflichtversicherung für niedergelassene Ärzte: Umfassender Schutz vor schwerwiegenden Behandlungsfehlern, Personenschäden und existenzbedrohenden finanziellen Risiken.',
    canonical: `${origin}/versicherungen/berufshaftpflicht`
  },
  
  '/versicherungen/betriebshaftpflicht': {
    title: 'Betriebshaftpflicht für Arztpraxen | Praxis-Versichert.de',
    description: 'Essenzielle Betriebshaftpflichtversicherung für Arztpraxen und medizinische Einrichtungen: Vollständige Absicherung gegen unvorhersehbare Sach- und schwere Personenschäden im täglichen Praxisbetrieb.',
    canonical: `${origin}/versicherungen/betriebshaftpflicht`
  },
  
  '/versicherungen/praxisausfallversicherung': {
    title: 'Praxisausfallversicherung | Umsatzausfall absichern',
    description: 'Unverzichtbare Praxisausfallversicherung für Ärzte: Umfassende finanzielle Absicherung und Einkommensschutz bei längerer Krankheit, schweren Unfällen oder anderen Ausfallursachen.',
    canonical: `${origin}/versicherungen/praxisausfallversicherung`
  },
  
  '/versicherungen/inhaltsversicherung': {
    title: 'Inhaltsversicherung für Arztpraxen | Praxis-Versichert.de',
    description: 'Wichtige Inhaltsversicherung für Arztpraxen und Kliniken: Vollständiger Schutz für teure medizinische Geräte, wertvolle Praxiseinrichtung und komplettes Inventar gegen alle Risiken.',
    canonical: `${origin}/versicherungen/inhaltsversicherung`
  },
  
  '/versicherungen/elektronikversicherung': {
    title: 'Elektronikversicherung für Arztpraxen | IT-Schutz',
    description: 'Spezialisierte Elektronikversicherung für hochwertige medizinische Geräte und kritische IT-Systeme: Umfassende Absicherung gegen kostspielige technische Defekte und unerwartete Schäden.',
    canonical: `${origin}/versicherungen/elektronikversicherung`
  },
  
  '/versicherungen/rechtsschutz': {
    title: 'Rechtsschutzversicherung für Ärzte | Praxis-Versichert.de',
    description: 'Professionelle Rechtsschutzversicherung speziell für niedergelassene Ärzte und Arztpraxen: Vollständiger finanzieller Schutz bei komplexen rechtlichen Auseinandersetzungen und Streitigkeiten.',
    canonical: `${origin}/versicherungen/rechtsschutz`
  },

  // ===== WEITERE SEITEN =====
  '/cyberrisiken': {
    title: 'Cyber-Risiken für Arztpraxen | Bedrohungen & Schutz',
    description: 'Detaillierte Übersicht der größten aktuellen Cyber-Risiken für Arztpraxen: Ransomware-Angriffe, Phishing-Attacken, kritischer Datenverlust, teure DSGVO-Verstöße und weitere digitale Bedrohungen.',
    canonical: `${origin}/cyberrisiken`
  },
  
  '/cyberversicherung': {
    title: 'Cyberversicherung-Hub | Alle Informationen & Tarife',
    description: 'Ihre zentrale Anlaufstelle für professionelle Cyberversicherungen: Alle Tarife im Vergleich, Praxistypen, Städte-Informationen, Schadensfallbeispiele und kompetente Expertenberatung.',
    canonical: `${origin}/cyberversicherung`
  },
  
  '/cyberversicherung/vergleichsrechner': {
    title: 'Cyberversicherung Vergleichsrechner | Tarife vergleichen',
    description: 'Vergleichen Sie jetzt Cyberversicherungen für Ihre Arztpraxis im intelligenten Vergleichsrechner. Völlig kostenlos, unverbindlich und individuell an Ihre Bedürfnisse angepasst.',
    canonical: `${origin}/cyberversicherung/vergleichsrechner`
  },
  
  '/tarifwissen': {
    title: 'Tarifwissen Cyberversicherung | Expertenwissen',
    description: 'Umfassendes professionelles Expertenwissen zu Cyberversicherungs-Tarifen: Alle Deckungssummen, Selbstbehalte, Ausschlüsse und wichtige Vertragsdetails verständlich erklärt.',
    canonical: `${origin}/tarifwissen`
  },
  
  '/weitere-versicherungen': {
    title: 'Weitere Versicherungen für Arztpraxen | Komplettschutz',
    description: 'Vollständige Übersicht weiterer essenzieller Versicherungen für Arztpraxen: Berufshaftpflicht, Praxisausfall, Elektronikversicherung, Inhaltsversicherung und weitere wichtige Absicherungen.',
    canonical: `${origin}/weitere-versicherungen`
  },
  
  '/erstinformation': {
    title: 'Erstinformation | Versicherungsvermittlung gem. §15 VersVermV',
    description: 'Pflichtgemäße Erstinformation gemäß §15 VersVermV: Vollständige transparente Informationen zu unserem Unternehmen, offizieller Registrierung und transparenter Vergütungsstruktur.',
    canonical: `${origin}/erstinformation`
  },
  
  '/cyber-checkliste': {
    title: 'Kostenlose Cyber-Checkliste für Arztpraxen | Praxis-Versichert',
    description: 'Laden Sie jetzt die kostenlose professionelle Cyber-Sicherheits-Checkliste für Arztpraxen herunter. Schützen Sie Ihre Praxis wirksam in nur 10 einfachen praktischen Schritten.',
    canonical: `${origin}/cyber-checkliste`
  }
};

/**
 * Get SEO configuration for a specific URL path
 * @param path - The URL path (e.g., '/about', '/services')
 * @returns SEO configuration object or default configuration
 */
export const getSEOConfig = (path: string): SEOConfig => {
  // Normalize path (remove trailing slash except for root)
  const normalizedPath = path === '/' ? '/' : path.replace(/\/$/, '');
  
  // Return config if found, otherwise return default
  return seoConfigs[normalizedPath] || {
    title: 'Praxis-Versichert.de | Cyberversicherung für Arztpraxen',
    description: 'Spezialist für Cyberversicherungen im Gesundheitswesen. Schützen Sie Ihre Arztpraxis vor digitalen Bedrohungen.',
    canonical: `${origin}${normalizedPath}`
  };
};

/**
 * Get Open Graph image URL for a specific path
 * Falls back to default hero image if not specified
 */
export const getOGImage = (path: string): string => {
  const config = getSEOConfig(path);
  return config.image || `${origin}/assets/hero-medical-cyber-main.webp`;
};

/**
 * Get canonical URL for a specific path
 */
export const getCanonicalURL = (path: string): string => {
  const config = getSEOConfig(path);
  return config.canonical || `${origin}${path}`;
};
